// The one place confirm() dialogs render. The shell mounts it once (Shell.jsx), beside
// the toast region. It imports NO React Aria: the dialog itself (kit Modal and Buttons)
// is loaded lazily the first time a confirm is shown, so the shell's main bundle stays
// as it was (the 861 kB regression bf8e4a5 fixed came from exactly that kind of import).
// Static imports here: react, ./confirm.js and ../toast/toast.js only (no classes.js: a
// test walks this graph).
import { Component, lazy, Suspense, useEffect, useSyncExternalStore } from 'react';

import { toast, toastStore } from '../toast/toast.js';
import { confirmQueue, registerConfirmHost } from './confirm.js';

const defaultLoader = () => import('./ConfirmDialog.jsx');
let loader = defaultLoader;

/**
 * The lazy dialog and its import. There is NO retry: a failed dynamic import is cached in
 * the browser's module map for the life of the page, so importing again would fail the
 * same way. The lazy is kept, every later confirm() answers false, and the user is told
 * to reload (see notifyDialogFailure).
 */
export function createDialogImport(getLoader) {
  let cached = null;
  const load = () => Promise.resolve().then(() => getLoader()());
  return {
    load,
    component() {
      cached ??= lazy(load);
      return cached;
    },
  };
}

// The id of the failure toast last shown. While that toast is still visible, further
// failures add nothing: many requests failing before a reload show ONE toast. Once it is
// dismissed or has timed out, the next failed request shows it again.
let failureToastId = null;

function notifyDialogFailure() {
  const visible = failureToastId !== null && toastStore.snapshot().items.some((t) => t.id === failureToastId);
  if (visible) return;
  failureToastId = toast.show({
    tone: 'danger',
    title: "Couldn't open the confirmation",
    message: 'Reload the page, then try again.',
  });
}

export const dialogImport = createDialogImport(() => loader);

/** Test hook (not exported from the barrel): swap the dialog loader; null restores it. */
export function __setConfirmDialogLoader(fn) {
  loader = fn ?? defaultLoader;
}

/**
 * Catches a dialog that cannot load (a stale chunk after a deploy, a dropped network) or
 * render. The request is answered false so no await hangs and the shell never sees the
 * error; it is reported once, as a message only (no stack, no request body), and the user
 * gets a danger toast telling them to reload (the failed import cannot be retried).
 */
export class DialogBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.error(`@platform/ui: the confirm dialog failed to load or render (${error?.message ?? 'unknown error'}); the confirm was answered no`);
    // Settle the request FIRST: a throwing toast subscriber must never leave the
    // awaiting confirm() hanging.
    this.props.onFail();
    try { notifyDialogFailure(); } catch { /* the user could not be told; the request is already answered */ }
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

export default function ConfirmHost() {
  useEffect(() => registerConfirmHost(), []);
  const head = useSyncExternalStore(confirmQueue.subscribe, confirmQueue.current, () => null);
  if (!head) return null;
  const ConfirmDialog = dialogImport.component();
  // Keyed by id: the next queued request gets a fresh boundary and dialog (and fresh
  // initial focus).
  return (
    <DialogBoundary key={head.id} entry={head} onFail={() => confirmQueue.settle(head.id, false)}>
      <Suspense fallback={null}>
        <ConfirmDialog entry={head} onSettle={confirmQueue.settle} />
      </Suspense>
    </DialogBoundary>
  );
}
